import React, { useContext, useEffect, useState } from "react";
import { QuotationContext } from "../../../../contexts/QuotationContext";
import { MtplQuotation } from "../../../../model/quotation";
import { useMediaQuery } from "@material-ui/core";
import { QuotationCardContext } from "../../../../contexts/QuotationCardContext";
import { CASCOSummaryPackage } from "../../../../components/SalesPackage/SalesSummaryPackage/CASCOSummaryPackage";
import { findVehicleGroup, OwnerIsInsuredOptions, VehicleTypeNames, VehicleType } from "../../../../model/vehicle";
import { MTPLSummaryPackage } from "../../../../components/SalesPackage/SalesSummaryPackage/MTPLSummaryPackage";
import { FormTitle } from "../../../../components/Form/FormTitle";
import { strings } from "../../../../strings/strings";
import { FormRow } from "../../../../components/Form/FormRow";
import { addDays, addYears } from '../../../../utils/dateUtils'
import moment from "moment/moment";
import { FormItem } from "../../../../components/Form/FormItem";
import { SimpleTooltip } from "../../../../components/SimpleTooltip";
import { translit } from "../../../../utils/utils";
import { OutputValue } from "../../../../components/inputs/OutputValue";
import { VinInfoMessage } from "../../../../components/InfoMessage/VinInfoMessage";
import { calculateMTPLDuration } from "../../../../api/durationApiClient";
import { CalculatedMTPLDuration } from "../../../../model/duration";
import { StepControls } from "../../../../components/Scenario/Step/StepControls";
import * as log from "../../../../utils/log";
import { LogFunctionalModule } from "../../../../utils/log";
import {
    citiesShortcut,
    convertContextNameFunction,
    documentTypeOptions,
    formatResponseForAutocomplete,
    formatSelectedOptionForAutocomplete,
    getMTPLAgree1CheckboxLabel,
    getMTPLAgree1SpanLabel
} from "./formHelpers";
import { getAgreeAboutProductLabel, ProductType } from "../../other/agreementCheckbox";
import { useTabCloseTracking, EventTypeKey } from "../hooks/useTabCloseTracking";
import { useFormikContext } from "formik";
import { ParametrizedField } from "../../../../components/ParametrizedField";
import { RadioGroup } from "../../../../components/inputs/RadioGroup";
import { InputText } from "../../../../components/inputs/InputText";
import { FormItem as FormItemControl } from "../../../../components/inputs/FormItem";
import { DatePicker } from "../../../../components/inputs/DatePicker";
import { InputTextMask } from "../../../../components/inputs/InputTextMask";
import { Checkbox } from "../../../../components/inputs/Checkbox";
import { fieldsName } from "./consts";
import FocusError from "../../../../components/FocusError";
import useSwitchToNewContent from "../../../../utils/hooks/useSwitchToNewContent";

interface FormContext {
    quotation: MtplQuotation
    agree1: boolean
    agree2: boolean
}

interface Props {
    backStepHandler: (quotation: MtplQuotation) => void
}

export const Form = ({ backStepHandler }: Props) => {
    const formProps = useFormikContext<FormContext>();
    const { values, setFieldValue, initialValues, handleSubmit, submitForm } = formProps

    const {
        quotation,
        agreeOption1,
        agreeInformOption,
        setAgreeOption1,
        setAgreeInformOption,
        selectedProgram,
        tarrificationProgram,
        saveTempValues,
    } = useContext<QuotationContext<MtplQuotation>>(QuotationContext);
    const [ownerIsInsured, setOwnerIsInsured] = useState<string>(quotation.cascoAdditionalCoverage.ownerIsInsured ? 'true' : 'false');
    const isMobile = useMediaQuery('(max-width: 1023px)');
    const { nextIsAvailable } = useContext(QuotationCardContext);

    const showNewContent = useSwitchToNewContent();

    useEffect(() => {
        saveTempValues("quotation", JSON.stringify(values.quotation))
    }, [values.quotation, saveTempValues]);

    const { email } = values?.quotation?.mtplContract?.primaryInsured || {};
    useTabCloseTracking(email, EventTypeKey.AbandonedOscpv);

    return (
        <>
            <div className="personal-info-container__package-overview">
                {
                    initialValues.quotation.cascoAdditionalCoverage.premium ?
                        <CASCOSummaryPackage
                            tarrificationProgram={tarrificationProgram}
                            packageName={selectedProgram.packageName}
                            vehicleGroup={findVehicleGroup(initialValues.quotation.mtplContract.vehicle.vehicleType)}
                            premium={initialValues.quotation.totalPremium}
                            deductible={initialValues.quotation.mtplContract.deductible}
                            mtplContract={initialValues.quotation.mtplContract.premium}
                            cascoAdditionalCoverage={initialValues.quotation.cascoAdditionalCoverage.premium}
                            vtplAdditionalCoverage={initialValues.quotation.vtplAdditionalCoverage.premium} />
                        :
                        <MTPLSummaryPackage
                            tarrificationProgram={tarrificationProgram}
                            vehicleGroup={findVehicleGroup(initialValues.quotation.mtplContract.vehicle.vehicleType)}
                            premium={initialValues.quotation.totalPremium}
                            deductible={initialValues.quotation.mtplContract.deductible}
                            mtplContract={initialValues.quotation.mtplContract.premium}
                            vtplAdditionalCoverage={initialValues.quotation.vtplAdditionalCoverage.premium} />
                }
            </div>
            <form onSubmit={handleSubmit} id={"form"} className="personal-info-form form">
                <div className="personal-info-form__sub-block">
                    <FormTitle>
                        <h1>{strings.personalInfoData}</h1>
                    </FormTitle>

                    <FormRow>
                        <ParametrizedField
                            contextName={fieldsName.primaryInsuredSurname}
                            convertContextNameFunction={convertContextNameFunction}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                        />
                        <ParametrizedField
                            contextName={fieldsName.primaryInsuredGivenName}
                            convertContextNameFunction={convertContextNameFunction}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                        />
                        <ParametrizedField
                            contextName={fieldsName.primaryInsuredMiddleName}
                            convertContextNameFunction={convertContextNameFunction}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                        />
                        <ParametrizedField
                            contextName={fieldsName.primaryInsuredBirthDate}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            convertContextNameFunction={convertContextNameFunction}
                            minDate={addYears(new Date(), -100)}
                            maxDate={addYears(new Date(), -18)}
                        />
                        <FormItem colspan={isMobile ? 1 : 2}>
                            <ParametrizedField
                                contextName={fieldsName.primaryInsuredLivingAddress}
                                convertContextNameFunction={convertContextNameFunction}
                                quotationName={'quotation.mtplContract.'}
                                context={QuotationCardContext}
                                defaultLabel={strings.personalInfoLivingAddress}
                            />
                        </FormItem>
                        <ParametrizedField
                            contextName={fieldsName.primaryInsuredMobileNumber}
                            convertContextNameFunction={convertContextNameFunction}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            mask={"+38(099)999-99-99"}
                            placeholder="+38(0__)___-__-__"
                        />
                        <ParametrizedField
                            contextName={fieldsName.primaryInsuredEmail}
                            convertContextNameFunction={convertContextNameFunction}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            placeholder={'example@mail.com'}
                        />
                    </FormRow>
                </div>
                <div className="personal-info-form__sub-block" style={{ marginBottom: 32 }}>
                    <FormRow>
                        <SimpleTooltip id={translit(strings.personalInfoResident)} placement={"right"} title={
                            <span>{strings.personalInfoResidentTooltip}</span>}>
                            <div className='personal-info-form__resident'>
                                <ParametrizedField
                                    contextName={fieldsName.primaryInsuredResident}
                                    convertContextNameFunction={convertContextNameFunction}
                                    quotationName={'quotation.mtplContract.'}
                                    context={QuotationCardContext}
                                    defaultLabel={strings.personalInfoResident}
                                />
                            </div>
                        </SimpleTooltip>
                    </FormRow>
                    <FormRow>
                        <ParametrizedField
                            contextName={fieldsName.primaryInsuredResidentCountry}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            convertContextNameFunction={convertContextNameFunction}
                            shortcut={citiesShortcut()}
                            formatResponse={formatResponseForAutocomplete}
                            formatValue={formatSelectedOptionForAutocomplete}
                        />
                        <ParametrizedField
                            contextName={fieldsName.primaryInsuredIpn}
                            convertContextNameFunction={convertContextNameFunction}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            mask={"9999999999"}
                        />
                        <ParametrizedField
                            contextName={fieldsName.primaryInsuredRegistrationDocumentType}
                            convertContextNameFunction={(name) => convertContextNameFunction(name).replaceAll("type", "documentType")}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            defaultOptions={documentTypeOptions}
                        />
                        <ParametrizedField
                            contextName={fieldsName.primaryInsuredRegistrationDocumentNumber}
                            convertContextNameFunction={convertContextNameFunction}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            mask={"**********"}
                            defaultLabel={strings.registrationDocumentNumber}
                        />
                        <ParametrizedField
                            contextName={fieldsName.primaryInsuredRegistrationDocumentIssueOrganization}
                            convertContextNameFunction={convertContextNameFunction}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            defaultLabel={strings.registrationDocumentIssueOrganization}
                        />
                        <ParametrizedField
                            contextName={fieldsName.primaryInsuredRegistrationDocumentIssueDate}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            convertContextNameFunction={convertContextNameFunction}
                            minDate={addYears(new Date(), -100)}
                            maxDate={new Date()}
                        />
                    </FormRow>
                </div>
                {quotation.cascoAdditionalCoverage.covered && (
                    <FormRow columns={1}>
                        <div className='personal-info-form__owner no-border'>
                            <RadioGroup
                                name={'covered'}
                                value={ownerIsInsured}
                                options={OwnerIsInsuredOptions}
                                onChange={(selectedOwnerIsInsured) => {
                                    setOwnerIsInsured(selectedOwnerIsInsured);
                                    setFieldValue("quotation.cascoAdditionalCoverage.ownerIsInsured", (selectedOwnerIsInsured === "true"));
                                    if (selectedOwnerIsInsured === "true") {
                                        setFieldValue("quotation.cascoAdditionalCoverage.ownerName", "");
                                        setFieldValue("quotation.cascoAdditionalCoverage.ownerAddress", "");
                                        setFieldValue("quotation.cascoAdditionalCoverage.ownerBirthDate", null);
                                    }
                                }}
                            />
                        </div>
                    </FormRow>
                )}
                {ownerIsInsured === 'false' && quotation.cascoAdditionalCoverage.covered && (
                    <FormRow>
                        <FormItemControl name={'quotation.cascoAdditionalCoverage.ownerName'}>
                            <InputText
                                label={strings.vehicleOwnerName}
                                disabled={false}
                                hidden={false}
                                type={'TEXT'}
                            />
                        </FormItemControl>
                        <FormItemControl name={'quotation.cascoAdditionalCoverage.ownerAddress'}>
                            <InputText
                                label={strings.vehicleOwnerAddress}
                                disabled={false}
                                hidden={false}
                                type={'TEXT'}
                            />
                        </FormItemControl>

                        {quotation.mtplContract.vehicle.ownerPartyType === "PERSON" && (
                            <FormItemControl name={'quotation.cascoAdditionalCoverage.ownerBirthDate'}>
                                <DatePicker
                                    label={strings.vehicleOwnerBirthDate}
                                    placeholder={strings.datePlaceholder}
                                    minDate={addYears(new Date(), -100)}
                                    maxDate={addYears(new Date(), -18)}
                                    disabled={false}
                                    hidden={false}
                                />
                            </FormItemControl>

                        )}
                    </FormRow>
                )}

                <div className="personal-info-form__sub-block">
                    <FormTitle>
                        <h1>{strings.vehicleData}</h1>
                    </FormTitle>

                    <div className="personal-info-form__vehicle-data">
                        <FormRow columns={4}>
                            <OutputValue
                                name={'quotation.mtplContract.vehicle.vehicleType'}
                                outputValue={initialValues.quotation.mtplContract.vehicle.vehicleType}
                                label={strings.category}
                            />
                            <OutputValue
                                name={'quotation.mtplContract.vehicle.vehicleType'}
                                outputValue={VehicleTypeNames[initialValues.quotation.mtplContract.vehicle.vehicleType]}
                                label={strings.vehicleType}
                            />
                            <OutputValue
                                name={'quotation.mtplContract.vehicle.brand'}
                                outputValue={initialValues.quotation.mtplContract.vehicle.brand.name}
                                label={strings.brand}
                            />
                            <OutputValue
                                name={'quotation.mtplContract.vehicle.constructionYear'}
                                outputValue={initialValues.quotation.mtplContract.vehicle.constructionYear.toString()}
                                label={strings.vehicleConstructionYear}
                            />
                        </FormRow>
                    </div>

                    <FormRow>
                        <ParametrizedField
                            contextName={fieldsName.vehicleModel}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            formatResponse={formatResponseForAutocomplete}
                            formatValue={formatSelectedOptionForAutocomplete}
                        />
                        <ParametrizedField
                            contextName={fieldsName.vehicleVin}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            mask={"*****************"}
                            fieldInfoText={strings.whatIsVin}
                            messageInfoObj={<VinInfoMessage />}
                            toUpperCase={true}
                        />
                        <ParametrizedField
                            contextName={fieldsName.vehiclePlateNumber}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            defaultLabel={strings.vehiclePlateNumber}
                            small={true}
                            plateNumber={true}
                            formatResponse={(option: string) => {
                                return option.replaceAll(" ", "");
                            }}
                        />
                        {showNewContent && (<>
                            <ParametrizedField
                                contextName={fieldsName.engineType}
                                quotationName={'quotation.mtplContract.'}
                                context={QuotationCardContext}
                            />
                            <ParametrizedField
                                contextName={fieldsName.vehicleBatteryCapacity}
                                quotationName={'quotation.mtplContract.'}
                                context={QuotationCardContext}
                                tooltipLabel={'Якщо в техпаспорті не вказані параметри, встановіть в полі значення 1'}
                            />
                            <ParametrizedField
                                contextName={fieldsName.vehicleEngineCapacity}
                                quotationName={'quotation.mtplContract.'}
                                context={QuotationCardContext}
                                tooltipLabel={'Якщо в техпаспорті не вказані параметри, встановіть в полі значення 1'}
                            />
                            <ParametrizedField
                                contextName={fieldsName.vehicleTotalWeight}
                                quotationName={'quotation.mtplContract.'}
                                context={QuotationCardContext}
                            />
                            <ParametrizedField
                                contextName={fieldsName.vehicleOwnWeight}
                                quotationName={'quotation.mtplContract.'}
                                context={QuotationCardContext}
                            />
                            <ParametrizedField
                                contextName={fieldsName.vehicleNumberSeating}
                                quotationName={'quotation.mtplContract.'}
                                context={QuotationCardContext}
                                tooltipLabel={'Якщо в техпаспорті не вказані параметри, встановіть в полі значення 1'}
                            />
                        </>)}
                    </FormRow>
                </div>

                <div className="personal-info-form__sub-block">
                    <FormTitle>
                        <h1>{strings.contractStart}</h1>
                    </FormTitle>

                    <FormRow>
                        <ParametrizedField
                            contextName={fieldsName.startDate}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            minDate={addDays(new Date(), 1)}
                            maxDate={addDays(new Date(), 30)}
                            placeholder={strings.datePlaceholder}
                            onChangeListener={(option: string) => {
                                const date = moment(option, "YYYY-MM-DD").format("YYYY-MM-DD");
                                calculateMTPLDuration({
                                    startDate: date,
                                    requiredTO: initialValues.quotation.mtplContract.vehicle.requiredTO,
                                    nextToDate: initialValues.quotation.mtplContract.vehicle.nextToDate ? moment(initialValues.quotation.mtplContract.vehicle.nextToDate).format("YYYY-MM-DD") : null
                                })
                                    .then((response: CalculatedMTPLDuration) => {
                                        setFieldValue('quotation.mtplContract.duration', response.duration);
                                        setFieldValue('quotation.mtplContract.endDate', response.endDate);
                                    });
                            }}
                        />
                        <ParametrizedField
                            contextName={fieldsName.endDate}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                            placeholder={strings.datePlaceholder}
                        />
                        <ParametrizedField
                            contextName={fieldsName.duration}
                            quotationName={'quotation.mtplContract.'}
                            context={QuotationCardContext}
                        />
                    </FormRow>
                </div>

                <FormRow columns={1}>
                    <div className='personal-info-form__agree'>
                        <FormItemControl name={'agreeInform'} validation={{ custom: (value) => !value && strings.validation.required }}>
                            <Checkbox
                                label={getAgreeAboutProductLabel(ProductType.MTPL, initialValues.quotation)}
                                onChange={val => {
                                    setAgreeInformOption(val)
                                }}
                                value={agreeInformOption}
                            />
                        </FormItemControl>
                    </div>
                </FormRow>

                <FormRow columns={1}>
                    <div className='personal-info-form__agree'>
                        <FormItemControl name={'agree1'} validation={{ custom: (value) => !value && strings.validation.required }}>
                            <Checkbox
                                label={getMTPLAgree1CheckboxLabel(initialValues.quotation)}
                                onChange={val => {
                                    setAgreeOption1(val)
                                }}
                                value={agreeOption1}
                            />
                        </FormItemControl>
                    </div>
                </FormRow>

                <FormRow columns={1}>
                    <div className='personal-info-container__MTPLAgree1'>
                        {getMTPLAgree1SpanLabel(initialValues.quotation)}
                    </div>
                </FormRow>

                <FocusError />

                <StepControls
                    onNext={() => {
                        const date = new Date();
                        log.sendLog(date, LogFunctionalModule.STORE_EMTPL, 'INFO', "PERSONAL_INFO_FORM_NEXT_HANDLER", [{
                            quotation: quotation,
                            formProps: formProps
                        }], '')
                        submitForm()
                    }}
                    nextIsAvailable={nextIsAvailable}
                    onBack={() => {
                        const date = new Date();
                        log.sendLog(date, LogFunctionalModule.STORE_EMTPL, 'INFO', "PERSONAL_INFO_FORM_BACK_HANDLER", [{
                            quotation: quotation,
                            formProps: formProps
                        }], '')
                        backStepHandler(initialValues.quotation);
                    }}
                    leftSteps={1}
                />
            </form>
        </>
    )
}